import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Header } from '@/components/landing/Header';
import { supabase } from '@/lib/supabaseClient';

const Waitlist = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    interest: '',
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!formData.email || !formData.interest) {
      setError('Please complete all fields.');
      return;
    }

    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      setError('Waitlist is not configured yet. Please try again later.');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: insertError } = await supabase.from('waitlist').insert({
        email: formData.email,
        interest_statement: formData.interest,
      });

      if (insertError) {
        const isDuplicate =
          insertError.code === '23505' ||
          insertError.message.toLowerCase().includes('duplicate') ||
          insertError.details?.toLowerCase().includes('duplicate') ||
          insertError.hint?.toLowerCase().includes('duplicate');

        if (isDuplicate) {
          setError('You are already on the waitlist with this email.');
          return;
        }

        setError('Something went wrong. Please try again.');
        return;
      }

      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <div 
        className="absolute inset-0 w-full"
        style={{
          backgroundImage: `
            linear-gradient(rgb(100 100 100 / 0.03) 1px, transparent 1px),
            linear-gradient(to right, rgb(100 100 100 / 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          zIndex: -1
        }}
      />

      <Header />

      <main className="flex-1 pt-[72px]">
        <section className="container mx-auto px-4 py-20">
          <div className="mx-auto max-w-[48rem] space-y-8 text-center">
            <div className="space-y-3">
              <h1 className="text-4xl font-medium tracking-tight sm:text-5xl">
                Join the Levot waitlist
              </h1>
              <p className="text-lg text-muted-foreground">
                Tell us about your workflow and we will reach out with next steps.
              </p>
            </div>

            <div className="rounded-2xl bg-white p-8 shadow-[0_1px_3px_0_rgb(0,0,0,0.1)]">
              {submitted ? (
                <div className="space-y-3 text-center">
                  <h2 className="text-2xl font-medium">Thanks for joining</h2>
                  <p className="text-muted-foreground">
                    We will review your request and contact you soon.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="rounded-full px-6"
                  >
                    Back to Home
                  </Button>
                </div>
              ) : (
                <form className="space-y-6 text-left" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <Label htmlFor="email">Work email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, email: event.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interest">What are you interested in?</Label>
                    <Textarea
                      id="interest"
                      name="interest"
                      placeholder="Client intake automation, retainer delivery, medical releases, e-signatures, or other needs."
                      rows={5}
                      value={formData.interest}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, interest: event.target.value }))
                      }
                      required
                    />
                  </div>
                  {error ? (
                    <p className="text-sm text-red-600">{error}</p>
                  ) : null}
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Join the Waitlist'}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    We will never share your information.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Waitlist;
